import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { useEnquiryFlowStore } from "@/store/enquiryFlowStore";
import { useSurveySheetStore } from "@/store/surveySheetStore";

// Mirrors the sheet's step chain into the URL hash so the browser/Android
// back button walks it one step at a time instead of leaving the page. Every
// forward step pushes one `#<step>` entry; a back (button or arrow) pops one
// and runs stepBack(). Closing the sheet any other way unwinds the entries
// that are left. Also covers the standalone survey sheet (home, listing...).
const STEP_HASH = {
  mandatory: "#get-in-touch",
  survey: "#survey",
  review: "#review",
  success: "#submitted"
};
let depth = 0;
let unwinding = false;
const pending = {
  afterUnwind: null
};
function currentStep() {
  if (useSurveySheetStore.getState().isOpen) return "survey";
  return useEnquiryFlowStore.getState().activeStep;
}
function stepBack() {
  const flow = useEnquiryFlowStore.getState();
  const survey = useSurveySheetStore.getState();
  if (survey.isOpen) {
    const back = survey.onBack;
    survey.close();
    back?.();
    return;
  }
  if (flow.activeStep === "mandatory") {
    if (flow.isEditingFromReview) flow.goToReview({});else flow.close();
  } else if (flow.activeStep === "review") {
    survey.open({
      initialAnswers: Object.keys(flow.surveyAnswers).length ? flow.surveyAnswers : undefined,
      onComplete: answers => flow.goToReview({
        surveyAnswers: answers
      }),
      onSkip: answers => flow.goToReview({
        surveyAnswers: answers
      }),
      onBack: () => flow.backToMandatory(),
      context: "enquiryFlow"
    });
  } else {
    flow.close();
  }
}
function unwind() {
  if (depth === 0) return;
  const steps = depth;
  depth = 0;
  unwinding = true;
  window.history.go(-steps);
}
export function navigateBack() {
  if (depth > 0) window.history.back();else stepBack();
}

// Closes the sheet, then runs `then` once its history entries are gone, so a
// router.push doesn't race the pending history.go().
export function leaveFlow(then) {
  if (depth > 0) pending.afterUnwind = then;else then?.();
  useSurveySheetStore.getState().close();
  useEnquiryFlowStore.getState().close();
}
export function useEnquiryFlowHistory(stepKey) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
  let t1;
  let t2;
  if ($[1] !== stepKey) {
    t1 = () => {
      if (stepKey === null) {
        unwind();
        return;
      }
      const hash = STEP_HASH[stepKey];
      if (window.location.hash === hash) {
        return;
      }
      window.history.pushState(null, "", `${window.location.pathname}${window.location.search}${hash}`);
      depth = depth + 1;
    };
    t2 = [stepKey];
    $[1] = stepKey;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
}
function _temp2() {
  const onPopState = _temp;
  window.addEventListener("popstate", onPopState);
  return () => window.removeEventListener("popstate", onPopState);
}
function _temp() {
  if (unwinding) {
    unwinding = false;
    const next = pending.afterUnwind;
    pending.afterUnwind = null;
    if (next) {
      setTimeout(next, 0);
    }
    return;
  }
  const step = currentStep();
  if (!step) {
    return;
  }
  depth = Math.max(0, depth - 1);
  stepBack();
}